import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { getAccessToken } from "../../awsAuth";
import { categoryVisualMap } from "../CreateListing/categoryVisualMap";
import { compressImage } from "../../utils/imageCompress";
import "../CreateListing/CreatePost.scss";
import "./EditPost.scss";

import DynamicHeader from "../Header/DynamicHeader";
import Alerts from "../Alerts/Alerts";

const EditPost = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { userId } = useAuth();

    const getAuthHeaders = useCallback(async (extra = {}) => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error("NO_TOKEN");
    }

    return {
      ...extra,
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [message, setMessage] = useState("");

  const [text, setText] = useState("");
  const [photoObj, setPhotoObj] = useState(null);

  const [category, setCategory] = useState("");
  const [showCategories, setShowCategories] = useState(false);

  const [createdAt, setCreatedAt] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Dirty + Alerts
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);

  const pendingLeaveRef = useRef(null);
  const pendingHrefRef = useRef(null);
  const pendingHistoryBackRef = useRef(false);

  const allowPopRef = useRef(false);
  const lastUrlRef = useRef(
    window.location.pathname + window.location.search + window.location.hash
  );

  const selectRef = useRef(null);
  const fileInputRef = useRef(null);
  const textRef = useRef(null);
  const categoryTriggerRef = useRef(null);

  const categories = Object.keys(categoryVisualMap);
  const navHistory = window.history;

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!showCategories) return;
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setShowCategories(false);
      }
    };
    const handleEsc = (e) => e.key === "Escape" && setShowCategories(false);
    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [showCategories]);

  const stableStringify = useCallback((obj) => {
    const seen = new WeakSet();
    const sorter = (value) => {
      if (value === null || typeof value !== "object") return value;
      if (seen.has(value)) return value;
      seen.add(value);

      if (Array.isArray(value)) return value.map(sorter);

      const keys = Object.keys(value).sort();
      const out = {};
      keys.forEach((k) => (out[k] = sorter(value[k])));
      return out;
    };

    try {
      return JSON.stringify(sorter(obj));
    } catch (e) {
      return "";
    }
  }, []);

  const buildSnapshot = useCallback(
    (override = {}) => {
      const normalized = {
        createdAt: override.createdAt ?? createdAt ?? null,
        text: (override.text ?? text ?? "").toString(),
        category: (override.category ?? category ?? "").toString(),

        photo: override.photo ?? photoObj?.url ?? null,
        isLocalPhoto: !!(override.isLocalPhoto ?? photoObj?.isLocal),
      };

      normalized.text = normalized.text;

      normalized.photo =
        normalized.photo && typeof normalized.photo === "string"
          ? normalized.photo
          : null;

      return normalized;
    },
    [createdAt, text, category, photoObj]
  );

  const buildSnapshotFromFetched = useCallback((data) => {
    return {
      createdAt: data?.createdAt || null,
      text: (data?.text || "").toString(),
      category: (data?.category || "").toString(),
      photo:
        data?.photo && typeof data.photo === "string" ? data.photo : null,
      isLocalPhoto: false,
    };
  }, []);

  const currentSnapshot = useMemo(() => buildSnapshot(), [buildSnapshot]);

  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;

    const a = stableStringify(initialSnapshot);
    const b = stableStringify(currentSnapshot);

    return a !== b;
  }, [initialSnapshot, currentSnapshot, stableStringify]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const onDocClickCapture = (e) => {
      if (!isDirty) return;

      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const a = e.target?.closest?.("a[href]");
      if (!a) return;

      const href = a.getAttribute("href");
      if (!href) return;

      const target = a.getAttribute("target");
      if (target && target !== "_self") return;
      if (a.hasAttribute("download")) return;
      if (href.startsWith("mailto:") || href.startsWith("tel:")) return;

      if (href.startsWith("http://") || href.startsWith("https://")) {
        try {
          const u = new URL(href, window.location.origin);
          if (u.origin !== window.location.origin) return;
        } catch (err) {
          return;
        }
      }

      const resolved = new URL(href, window.location.origin);
      const to = resolved.pathname + resolved.search + resolved.hash;
      const from =
        window.location.pathname + window.location.search + window.location.hash;

      if (to === from) return;

      e.preventDefault();
      e.stopPropagation();

      pendingHrefRef.current = to;
      pendingLeaveRef.current = null;
      pendingHistoryBackRef.current = false;

      setShowUnsavedAlert(true);
    };

    document.addEventListener("click", onDocClickCapture, true);
    return () => document.removeEventListener("click", onDocClickCapture, true);
  }, [isDirty]);

  useEffect(() => {
    const onPopState = () => {
      const currentUrl =
        window.location.pathname + window.location.search + window.location.hash;

      if (allowPopRef.current) {
        allowPopRef.current = false;
        lastUrlRef.current = currentUrl;
        return;
      }

      if (!isDirty) {
        lastUrlRef.current = currentUrl;
        return;
      }

      try {
        navHistory.pushState(null, "", lastUrlRef.current);
      } catch (e) {}

      pendingHistoryBackRef.current = true;
      pendingLeaveRef.current = null;
      pendingHrefRef.current = null;

      setShowUnsavedAlert(true);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isDirty, navHistory]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`/api/posts/${postId}`);
        if (!res.data) {
          setMessage("Пост не знайдено");
          return;
        }
        if (userId && res.data.userId && userId !== res.data.userId) {
          setMessage("У вас немає прав на редагування цього поста");
          return;
        }

        setText(res.data.text || "");
        setCategory(res.data.category || "");
        setCreatedAt(res.data.createdAt || null);

        if (res.data.photo) {
          setPhotoObj({
            url: res.data.photo,
            uploading: false,
            progress: 100,
            isLocal: false,
          });
        } else {
          setPhotoObj(null);
        }

        const snap = buildSnapshotFromFetched(res.data);
        setInitialSnapshot(snap);

        lastUrlRef.current =
          window.location.pathname +
          window.location.search +
          window.location.hash;
      } catch (e) {
        console.error("load post:", e);
        setMessage("Не вдалося завантажити пост");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [postId, userId, buildSnapshotFromFetched]);

  const triggerFileDialog = () => fileInputRef.current?.click();

  const scrollToAndMark = useCallback((ref, className = "field-error") => {
    const node = ref?.current ?? ref;
    if (!node) return;
    node.classList.add(className);
    node.scrollIntoView?.({ behavior: "smooth", block: "center" });
    const inputEl = node.querySelector?.("input, textarea, select") || node;
    inputEl.focus?.();
    setTimeout(() => node.classList.remove(className), 2400);
  }, []);

    const uploadSingleFile = async (file, onProgress) => {
    const compressed = await compressImage(file, {
      maxSide: 1600,
      mimeType: "image/webp",
      quality: 0.82,
    });

    const fd = new FormData();
    fd.append("photo", compressed, compressed.name);

    const headers = await getAuthHeaders({
      "Content-Type": "multipart/form-data",
    });

    const res = await axios.post("/api/posts/upload-photo", fd, {
      headers,
      onUploadProgress: (evt) => {
        if (!evt.total) return;
        const pct = Math.round((evt.loaded * 100) / evt.total);
        onProgress?.(pct);
      },
    });

    return res.data.photoUrl;
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const tmpUrl = URL.createObjectURL(file);
    setPhotoObj({ url: tmpUrl, uploading: true, progress: 0, isLocal: true });

    try {
      setUploading(true);
      const uploadedUrl = await uploadSingleFile(file, (pct) => {
        setPhotoObj((prev) => (prev ? { ...prev, progress: pct } : prev));
      });

      setPhotoObj({
        url: uploadedUrl,
        uploading: false,
        progress: 100,
        isLocal: false,
      });
        } catch (err) {
      console.error("upload:", err);

      if (String(err?.message || "") === "NO_TOKEN") {
        setMessage("Сесія неактивна. Увійдіть знову");
      } else {
        setMessage(err?.response?.data?.error || "Помилка завантаження фото");
      }

      setPhotoObj((prev) =>
        prev ? { ...prev, uploading: false, error: true } : prev
      );
    } finally {
      setUploading(false);
      if (e?.target) e.target.value = "";
    }
  };

  const handleRemovePhoto = () => setPhotoObj(null);

  const attemptLeave = useCallback(
    (leaveFn) => {
      if (!isDirty) {
        leaveFn();
        return;
      }
      pendingLeaveRef.current = leaveFn;
      pendingHrefRef.current = null;
      pendingHistoryBackRef.current = false;
      setShowUnsavedAlert(true);
    },
    [isDirty]
  );

  const clearLeaveContext = () => {
    pendingLeaveRef.current = null;
    pendingHrefRef.current = null;
    pendingHistoryBackRef.current = false;
  };

  const closeUnsavedAlert = () => {
    setShowUnsavedAlert(false);
    clearLeaveContext();
  };

  const proceedWithoutSaving = () => {
    setShowUnsavedAlert(false);

    if (pendingHrefRef.current) {
      const to = pendingHrefRef.current;
      clearLeaveContext();
      navigate(to);
      return;
    }

    if (pendingHistoryBackRef.current) {
      clearLeaveContext();
      allowPopRef.current = true;
      navHistory.back();
      return;
    }

    if (typeof pendingLeaveRef.current === "function") {
      const fn = pendingLeaveRef.current;
      clearLeaveContext();
      fn();
      return;
    }

    clearLeaveContext();
  };

    const savePost = async () => {
    if (!createdAt) {
      setMessage("Відсутній ключ createdAt");
      return false;
    }

    if (!text.trim() && !photoObj?.url) {
      setMessage("Додайте текст або фото");
      scrollToAndMark(textRef);
      return false;
    }

    try {
      const headers = await getAuthHeaders();

      await axios.patch(
        `/api/posts/${postId}`,
        {
          createdAt,
          text: text.trim(),
          photo: photoObj?.url || null,
          category: category || "",
        },
        { headers }
      );

      setMessage("Зміни збережено");

      const newSnap = buildSnapshot({
        text: text,
        category: category,
        createdAt: createdAt,
        photo: photoObj?.url || null,
        isLocalPhoto: false,
      });
      setInitialSnapshot(newSnap);

      return true;
    } catch (err) {
      console.error("save:", err);

      if (String(err?.message || "") === "NO_TOKEN") {
        setMessage("Сесія неактивна. Увійдіть знову");
        return false;
      }

      const m = err?.response?.data?.error || "Не вдалося зберегти зміни";
      setMessage(`${m}`);
      return false;
    }
  };

  const saveThenLeave = async () => {
    const ok = await savePost();
    if (!ok) return;

    setShowUnsavedAlert(false);

    if (pendingHrefRef.current) {
      const to = pendingHrefRef.current;
      clearLeaveContext();
      navigate(to);
      return;
    }

    if (pendingHistoryBackRef.current) {
      clearLeaveContext();
      allowPopRef.current = true;
      navHistory.back();
      return;
    }

    if (typeof pendingLeaveRef.current === "function") {
      const fn = pendingLeaveRef.current;
      clearLeaveContext();
      fn();
      return;
    }

    clearLeaveContext();
    setTimeout(() => navigate(-1), 250);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const ok = await savePost();
    if (!ok) return;
    setTimeout(() => navigate(-1), 600);
  };

  if (loading) {
    return (
      <>
        <DynamicHeader
          title="Редагувати допис"
          variant="glass"
          size="compact"
          hideOnScroll
          showBack
          onBack={() => attemptLeave(() => navigate(-1))}
          showMenu={false}
          rightNode={null}
          withSpacer={false}
        />

        <div className="local-header-spacer small" />

        <div className="edit-post-page">
          <div
            className="create-post edit-post ios-glass-card skeleton"
            aria-busy="true"
            aria-label="Завантаження форми редагування допису"
          >
            <h2>
              <span className="sk-line sk-title sk-shimmer" />
            </h2>

            <div className="sk-block sk-textarea sk-shimmer" />

            <div className="sk-block sk-category sk-shimmer" />

            <div className="sk-block sk-photo sk-shimmer" />

            <div className="sk-block sk-button sk-shimmer" />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <DynamicHeader
        title="Редагувати допис"
        variant="glass"
        size="compact"
        hideOnScroll
        showBack
        onBack={() => attemptLeave(() => navigate(-1))}
        showMenu={false}
        rightNode={null}
        withSpacer={false}
      />

      <Alerts
        open={showUnsavedAlert}
        variant="unsavedChanges"
        onClose={closeUnsavedAlert}
        onCancel={closeUnsavedAlert}
        onSecondary={proceedWithoutSaving}
        onPrimary={saveThenLeave}
      />

      <div className="local-header-spacer small" />

      <div className="edit-post-page">
        <div
          className={`create-post edit-post ios-glass-card ${
            mounted ? "enter" : ""
          }`}
          role="region"
          aria-label="Редагування допису"
        >
          <h2>Редагувати допис</h2>
          {message && <p className="message">{message}</p>}

          <form onSubmit={handleSave} className="create-post-form" noValidate>
            <textarea
              ref={textRef}
              className="pill-50"
              placeholder="Текст допису"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />

            <div className="custom-select-wrapper" ref={selectRef}>
              <div
                ref={categoryTriggerRef}
                className={`custom-select-trigger pill-50 ${
                  showCategories ? "open" : ""
                }`}
                onClick={() => setShowCategories((s) => !s)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") &&
                  setShowCategories((s) => !s)
                }
                aria-haspopup="listbox"
                aria-expanded={showCategories}
                aria-controls="edit-post-category-dropdown"
              >
                {category ? (
                  <div className="selected-category">
                    <div
                      className="category-icon"
                      style={{
                        backgroundImage: `url(${categoryVisualMap[category]})`,
                      }}
                    />
                    <span>{category}</span>
                  </div>
                ) : (
                  <span className="placeholder">Категорія (опційно)</span>
                )}
                <span className="chevron" aria-hidden="true">
                  ▾
                </span>
              </div>

              <div
                id="edit-post-category-dropdown"
                className={`custom-select-dropdown ${
                  showCategories ? "open" : "closed"
                }`}
                role="listbox"
                aria-hidden={!showCategories}
              >
                {categories.map((name, idx) => (
                  <div
                    key={name}
                    className="dropdown-item"
                    style={{ "--item-index": idx }}
                    onClick={() => {
                      setCategory(name);
                      setShowCategories(false);
                    }}
                    role="option"
                    aria-selected={category === name}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setCategory(name);
                        setShowCategories(false);
                      }
                    }}
                  >
                    <div
                      className="category-icon"
                      style={{
                        backgroundImage: `url(${categoryVisualMap[name]})`,
                      }}
                    />
                    <span>{name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="photo-upload">
              <label>Оновити фото (опційно)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: "none" }}
                disabled={uploading}
              />
              {!photoObj && (
                <button
                  type="button"
                  className="photo-tile empty add-one"
                  onClick={triggerFileDialog}
                  disabled={uploading}
                  aria-label="Додати фото"
                  title="Додати фото"
                >
                  <span className="plus">+</span>
                </button>
              )}
            </div>

            {photoObj && (
              <div className="photo-grid single">
                <div
                  className={`tile filled ${
                    photoObj.uploading ? "is-uploading" : ""
                  }`}
                >
                  <img src={photoObj.url} alt="Фото допису" />
                  {!photoObj.uploading && <span className="main-badge">Фото</span>}

                  {photoObj.uploading && (
                    <div className="progress-overlay" aria-label="Завантаження фото">
                      <div className="ring">
                        <svg viewBox="0 0 36 36" width="64" height="64" aria-hidden="true">
                          <path
                            className="bg"
                            d="M18 2.0845
                               a 15.9155 15.9155 0 0 1 0 31.831
                               a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            className="meter"
                            strokeDasharray={`${Math.max(
                              1,
                              Math.min(100, photoObj.progress ?? 0)
                            )}, 100`}
                            d="M18 2.0845
                               a 15.9155 15.9155 0 0 1 0 31.831
                               a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                        <div className="pct">{photoObj.progress ?? 0}%</div>
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    className="remove-btn"
                    onClick={handleRemovePhoto}
                    aria-label="Видалити фото"
                    title="Видалити фото"
                    disabled={photoObj.uploading}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            <div className="cta-sticky">
              <button
                type="submit"
                disabled={uploading || !isDirty}
                className={`primary-cta with-sheen ${
                  !isDirty || uploading ? "is-disabled" : ""
                }`}
                aria-disabled={uploading || !isDirty}
                title={!isDirty ? "Немає змін для збереження" : ""}
              >
                {uploading ? "Збереження..." : "Зберегти зміни"}
              </button>
            </div>

            <div className="bottom-safe-space" aria-hidden="true" />
          </form>
        </div>
      </div>
    </>
  );
};

export default EditPost;